/**
 * SSO returns the user to `/validate?code=…`, which makes the authorization
 * code part of a URL the browser is free to load more than once. Safari does
 * exactly that: it keeps `/validate` in the back/forward cache across the
 * post-login redirect, and `AppComponent`'s `onpageshow` handler reloads any
 * restored page. The reload re-runs `ValidateComponent` with the same code.
 *
 * SSO honours a code once, so the replay fails with "already been redeemed".
 * That looks like an auth failure, which starts a fresh login, which lands back
 * on `/validate` with a new code — a loop that only ends when the React handoff
 * circuit breaker gives up and parks the user on the legacy home page.
 *
 * Remembering which codes this tab has spent, and dropping the code from the
 * address bar as soon as it is read, makes a second execution harmless.
 */

const spentCodesKey = "spent-auth-codes";

/** Only recent codes can plausibly be replayed; keep the list from growing. */
const spentCodeHistory = 5;

const readSpentCodes = (): string[] => {
  try {
    const raw = sessionStorage.getItem(spentCodesKey);
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? (parsed as string[]) : [];
  } catch {
    return [];
  }
};

export const isAuthorizationCodeSpent = (code: string): boolean =>
  readSpentCodes().includes(code);

export const recordAuthorizationCodeSpent = (code: string): void => {
  const codes = [...readSpentCodes().filter((spent) => spent !== code), code];
  try {
    sessionStorage.setItem(
      spentCodesKey,
      JSON.stringify(codes.slice(-spentCodeHistory))
    );
  } catch {
    // Storage can be unwritable (Safari private browsing quota). Stripping the
    // code from the URL is the primary guard, so carry on without the record.
  }
};
